import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Layers,
  Shirt,
  Sparkles,
  Sofa,
  Smartphone,
  HeartPulse,
  Gem,
  Package,
} from "lucide-react";
import { CATEGORIES } from "@/lib/constants";

// Icon + accent palette per category. Falls back to Package for any future
// category added to /app/frontend/src/lib/constants.js.
const META = {
  Fashion: { icon: Shirt, tint: "#FFD1D1" },
  Beauty: { icon: Sparkles, tint: "#FFE0F0" },
  Home: { icon: Sofa, tint: "#FFF5D1" },
  Tech: { icon: Smartphone, tint: "#D1E8FF" },
  Wellness: { icon: HeartPulse, tint: "#D1FFEB" },
  Accessories: { icon: Gem, tint: "#E8D1FF" },
  General: { icon: Package, tint: "#F2F2F2" },
};

const goToStorefront = () => {
  document
    .getElementById("storefront")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const CategoryRail = ({ category, onChange }) => {
  const items = useMemo(
    () => [
      { key: "All", icon: Layers, tint: "#FFFFFF", label: "All" },
      ...CATEGORIES.map((c) => ({
        key: c,
        icon: META[c]?.icon || Package,
        tint: META[c]?.tint || "#F2F2F2",
        label: c,
      })),
    ],
    [],
  );

  const handlePick = (key) => {
    onChange?.(key);
    // small delay so the state update settles before we scroll
    setTimeout(goToStorefront, 60);
  };

  return (
    <section
      data-testid="category-rail"
      aria-label="Shop by category"
      className="w-full rounded-3xl border border-white/60 bg-white/65 backdrop-blur-xl backdrop-saturate-150 shadow-[0_8px_28px_rgba(0,0,0,0.06)] px-4 sm:px-6 py-4 sm:py-5"
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#064E3B] font-semibold">
          Shop by category
        </p>
        <button
          type="button"
          onClick={() => handlePick("All")}
          data-testid="category-view-all"
          className="text-[11px] uppercase tracking-[0.18em] font-medium text-neutral-600 hover:text-[#0B0C10] transition-colors"
        >
          View all
        </button>
      </div>

      {/* Horizontally scrolling rail. Hidden scrollbar; snap to start. */}
      <div
        className="flex gap-5 sm:gap-7 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-2 px-2"
        style={{ scrollbarWidth: "none" }}
        data-testid="category-rail-scroller"
      >
        {items.map((item, idx) => {
          const Icon = item.icon;
          const active = (item.key === "All" && (!category || category === "All")) || category === item.key;
          return (
            <motion.button
              key={item.key}
              type="button"
              onClick={() => handlePick(item.key)}
              data-testid={`category-rail-${item.key.toLowerCase()}`}
              aria-pressed={active}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04, duration: 0.35 }}
              className="snap-start shrink-0 flex flex-col items-center gap-2 outline-none"
            >
              {/* Circle */}
              <span
                className={`relative w-[68px] h-[68px] sm:w-[76px] sm:h-[76px] rounded-full flex items-center justify-center transition-all duration-300 ${
                  active
                    ? "ring-2 ring-[#E0B53C] ring-offset-2 ring-offset-white shadow-[0_8px_24px_rgba(224,181,60,0.35)]"
                    : "ring-1 ring-black/10 hover:ring-[#0B0C10]/40"
                }`}
                style={{ background: item.tint }}
              >
                <Icon
                  size={28}
                  strokeWidth={1.6}
                  className={active ? "text-[#022C22]" : "text-[#0B0C10]"}
                />
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 60%)",
                    }}
                  />
                )}
              </span>
              {/* Label */}
              <span
                className={`text-[11px] uppercase tracking-[0.16em] font-semibold text-center whitespace-nowrap ${
                  active ? "text-[#0B0C10]" : "text-neutral-600"
                }`}
              >
                {item.label}
              </span>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
};
